/**
 * Background notification poller for jobs the current user has just submitted.
 *
 * Pattern (server-side AgentRun):
 *   - User submits a contract → SubmitContractPage calls rememberSubmittedJob().
 *   - User is freed up to do other work — no progress page block.
 *   - This hook polls /api/v1/jobs/{id} every 8s for each remembered job.
 *   - When the job finishes (status=completed/failed), a toast appears with
 *     a "Open report" action that navigates to /jobs/{id}.
 *   - The job is then forgotten (one-shot notification).
 *
 * Uses sessionStorage so the watch survives a page reload.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const STORAGE_KEY = 'cmp.pendingJobNotifications.v1';
const POLL_INTERVAL_MS = 8_000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

interface PendingJob {
  job_id: string;
  file_name: string;
  remembered_at: number;
}

interface JobStatusResponse {
  id: string;
  status: string;
  review_status?: string | null;
  decision?: string | null;
}

function readPending(): PendingJob[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as PendingJob[]) : [];
  } catch {
    return [];
  }
}

function writePending(items: PendingJob[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage full — drop silently; notifications are best-effort */
  }
}

/** Add a freshly submitted job to the watchlist. Called from SubmitContractPage. */
export function rememberSubmittedJob(jobId: string, fileName: string): void {
  const next = readPending().filter((j) => j.job_id !== jobId);
  next.push({ job_id: jobId, file_name: fileName, remembered_at: Date.now() });
  // Cap watchlist to last 10 to avoid unbounded growth.
  writePending(next.slice(-10));
}

function decisionLabel(decision: string | null | undefined): string {
  if (!decision) return 'review';
  const normalized = decision.toUpperCase();
  if (normalized === 'APPROVED') return 'AI recommends APPROVE';
  if (normalized === 'APPROVED_WITH_ALERTS') return 'AI recommends APPROVE WITH ALERTS';
  if (normalized === 'REJECTED') return 'AI recommends REJECT';
  if (normalized === 'ESCALATED' || normalized === 'MANUAL_REVIEW_REQUIRED') {
    return 'AI requests human review';
  }
  return decision;
}

/**
 * Mount once (e.g. inside AppLayout). Polls the watchlist and emits a toast
 * with a "Open report" button when each job reaches a terminal status.
 */
export function useJobNotifications(): void {
  const { user } = useAuth();
  const navigate = useNavigate();
  // useNavigate may change identity per render but we want a stable callback
  // for the interval — capture it in a ref.
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const tick = async (): Promise<void> => {
      const pending = readPending();
      if (pending.length === 0) return;

      const survivors: PendingJob[] = [];
      for (const job of pending) {
        try {
          const detail = await api.get<JobStatusResponse>(`/api/v1/jobs/${job.job_id}`);
          const status = (detail.status || '').toLowerCase();
          const reviewStatus = (detail.review_status || '').toLowerCase();
          const pausedAtStage = reviewStatus.startsWith('paused_at_')
            ? reviewStatus.slice('paused_at_'.length)
            : null;

          // Under the per-stage HIL flow, a paused job stays at status='processing'
          // forever (waiting for human approval). Treat that as a notify-and-stop
          // state — toast the reviewer and drop it from the watchlist so we don't
          // poll the job endpoint every 8 s for the rest of the session.
          if (pausedAtStage) {
            toast.info(
              `${job.file_name}: paused at ${pausedAtStage} stage — awaiting your review`,
              {
                duration: Infinity,
                action: {
                  label: 'Open job',
                  onClick: () => { navRef.current(`/jobs/${job.job_id}`); },
                },
              },
            );
            // Drop from watchlist — one-shot notification.
            continue;
          }

          if (!TERMINAL_STATUSES.has(status)) {
            survivors.push(job);
            continue;
          }

          if (status === 'completed') {
            toast.success(
              `${job.file_name}: AI analysis complete · ${decisionLabel(detail.decision)}`,
              {
                duration: Infinity,
                action: {
                  label: 'Open report',
                  onClick: () => { navRef.current(`/jobs/${job.job_id}`); },
                },
              },
            );
          } else if (status === 'failed') {
            toast.error(`${job.file_name}: AI analysis failed. Open the job for details.`, {
              duration: Infinity,
              action: {
                label: 'Open job',
                onClick: () => { navRef.current(`/jobs/${job.job_id}`); },
              },
            });
          }
          // Drop from watchlist — one-shot notification.
        } catch (err: unknown) {
          // If the job 404s the user no longer has access (or it was deleted) —
          // forget it silently. Network errors keep it in the list.
          if (err instanceof ApiError && err.status === 404) continue;
          survivors.push(job);
        }
      }

      if (!cancelled) writePending(survivors);
    };

    // Run immediately, then on a poll interval.
    void tick();
    const handle = window.setInterval(() => { void tick(); }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [user]);
}
